import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  BatchAttachmentInfo,
  PayloadLocale,
  TemplateType,
  TemplateVariableDefinition,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

/**
 * Extended variable with runtime value and disabled state
 */
export interface PreparedVariable extends TemplateVariableDefinition {
  value: string | null;
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * Result from the prepare template endpoint
 */
export interface PreparedTemplateResult {
  template: {
    attachments?: BatchAttachmentInfo[];
    id: string;
    type: string;
    content: string;
    contentLexicalJson: string;
    language: string;
  };
  variables: PreparedVariable[];
  /**
   * Server-rendered default email subjects, one per supported affiliate locale.
   * Optional: an older backend omits it and every surface falls back to its
   * bundled i18n default. Only `it` is consumed today — the dashboard ships no
   * Italian bundle, so an Italian body would otherwise get an English subject.
   */
  defaultSubjects?: {
    en: string;
    fr: string;
    it: string;
  };
  context: {
    programId?: string;
    programName?: string;
    brandId: string;
    brandName?: string;
    hasCommission: boolean;
    commissionRate?: string;
  };
}

export type TemplateLanguage = PayloadLocale;

/**
 * Request payload for preparing a template
 */
interface PrepareTemplateRequest {
  programId: string;
  templateType: TemplateType;
  language?: TemplateLanguage;
}

/**
 * Prepare a template with context-aware variable population
 */
export const prepareTemplate = async (
  programId: string,
  templateType: TemplateType,
  language?: TemplateLanguage
): Promise<PreparedTemplateResult> => {
  const payload: PrepareTemplateRequest = {
    programId,
    templateType,
    ...(language ? { language } : {}),
  };

  const response = await api.post<ApiResponse<PreparedTemplateResult>>(
    '/dashboard/templates/prepare',
    payload
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to prepare template');
  }

  return response.data.data as PreparedTemplateResult;
};

interface UsePrepareTemplateOptions {
  enabled?: boolean;
  language?: TemplateLanguage;
}

/**
 * Hook to prepare a template with context-aware variable population
 *
 * @param programId - The program ID to prepare the template for
 * @param templateType - The type of template to prepare
 * @param options - Query options
 * @returns Query result with prepared template data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = usePrepareTemplate(
 *   selectedProgram.id,
 *   TemplateType.CREATOR_OUTREACH_EMAIL_INVITATION,
 *   { enabled: !!selectedProgram }
 * );
 *
 * // Access template content
 * const content = data?.template.contentLexicalJson;
 *
 * // Access variables with values and disabled state
 * const variables = data?.variables;
 * const disabledVars = variables?.filter(v => v.disabled);
 * ```
 */
export const usePrepareTemplate = (
  programId: string,
  templateType: TemplateType,
  options?: UsePrepareTemplateOptions
) => {
  const { enabled = true, language } = options || {};

  const finalEnabled = Boolean(programId) && Boolean(templateType) && enabled;

  return useQuery<PreparedTemplateResult, Error>({
    queryKey: ['prepareTemplate', programId, templateType, language ?? null],
    queryFn: () => prepareTemplate(programId, templateType, language),
    enabled: finalEnabled,
    gcTime: 1000 * 60 * 5, // 5 minutes
    staleTime: 1000 * 60, // 1 minute
    retry: 2,
    refetchOnWindowFocus: false,
  });
};
